<template>
  <div class="promo-code" :class="`promo-code--${variant}`">
    <transition name="promo-code-fade" mode="out-in">
      <!--- ADD STATE --->
      <form key="code.add" v-if="isAdding && !hasPromoCode" class="promo-code__add">
        <Input
          id="promoCode"
          class="promo-code__field"
          :value.sync="code"
          :placeholder="$t('checkout.promo_code.placeholder')"
          :variant="variant"
          :isDisabled="isSubmitting"
          v-bind="_props"
          v-on="$listeners"
        />
        <Button
          class="promo-code__submit-btn"
          :isDisabled="isSubmitDisabled"
          :isSubmitting="isSubmitting"
          v-data-test-id="'promo-code-submit'"
          @click.prevent="submitCode"
        >
          <template v-if="variant !== VARIANTS.tall">
            {{ $t('checkout.promo_code.submit') }}
          </template>
          <IconArrowRightBold v-else />
        </Button>
      </form>
      <!--- VIEW STATE --->
      <PromoView
        key="code.view"
        v-else-if="hasPromoCode"
        :variant="variant"
        :promotionAdjustment="promotionAdjustment"
        :hasPromoOrGiftCard="hasPromoCode"
        :discount="discount"
        :isSubmitting="isSubmitting"
        @click:remove-code="removeCode"
        @hook:mounted="$emit('promo-code-add')"
      />
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import to from 'await-to-js'
import { GTM_CONTEXTS } from '@/services/external-communication/gtm/gtm-data-layer'
import { isEmpty, includesValue } from '@/helpers/object'
import { startsWith } from '@/helpers/string'
import { formatToCurrency } from '@/helpers/currency-formatter'
import { MESSAGE_TYPES } from '@/stores/flash'
import PromoView from '@/components/_shared/Promo/PromoView.vue'
import Button from '@/components/_shared/Button.vue'
import Input from '@/components/_shared/Form/Input/Input.vue'
import { VARIANTS } from '@/components/_shared/Form/form-helper'
import IconArrowRightBold from '@/components/_icons/IconArrowRightBold.svg'

const GIFT_CARD_NUMBER_START = '638889001'

export default {
  name: 'PromoCode',
  components: {
    PromoView,
    IconArrowRightBold,
    Button,
    Input,
  },

  props: {
    flashContext: {
      type: String,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: VARIANTS.default,
      validator(value) {
        return includesValue(VARIANTS, value)
      },
    },
  },

  data() {
    return {
      code: '',
      isAdding: true,
      isSubmitting: false,
      errorCount: 0,
      hasSubmitError: false,
      VARIANTS,
    }
  },

  computed: {
    ...mapState({
      currency: state => state.store.currency,
      locale: state => state.page.locale,
    }),

    ...mapGetters({
      promotionAdjustment: 'order/promotionAdjustment',
      itemsLength: 'order/itemsLength',
      itemOutOfStock: 'order/itemOutOfStock',
      paths: 'order/paths',
      isCheckoutPage: 'page/isCheckoutPage',
    }),

    discount() {
      const amount = parseFloat(this.promotionAdjustment.amount)
      return formatToCurrency(amount, this.locale, this.currency)
    },

    hasPromoCode() {
      return !isEmpty(this.promotionAdjustment)
    },

    isSubmitDisabled() {
      return !this.code
    },
  },

  watch: {
    itemsLength: 'updateAddState',

    itemOutOfStock() {
      if (this.itemOutOfStock && this.isCheckoutPage) {
        window.location.assign(this.paths.cartUrl)
      }
    },
  },

  methods: {
    ...mapActions({
      addFlashMessage: 'flash/addMessage',
      clearPromoCode: 'order/clearPromoCode',
    }),

    updateAddState() {
      if (this.itemsLength <= 0) {
        this.isAdding = false
      }
    },

    async removeCode() {
      if (!this.hasPromoCode) return
      this.isSubmitting = true
      const [error] = await to(this.clearPromoCode(this.promotionAdjustment.code))
      if (!isEmpty(error)) {
        this.showFlashMessage(error.type, error.text)
      } else {
        this.isAdding = true
        this.code = ''
      }
      this.isSubmitting = false
    },

    showFlashMessage(type, text) {
      this.$store.dispatch('flash/addMessage', {
        text: text || this.$t('errors.something_went_wrong'),
        type: MESSAGE_TYPES[type],
        context: this.flashContext,
        ...(type === MESSAGE_TYPES.error ? { gtmContext: GTM_CONTEXTS.promoCodeSubmit } : {}),
      })
    },

    async submitCode() {
      if (startsWith(GIFT_CARD_NUMBER_START, this.code)) {
        this.showFlashMessage(
          MESSAGE_TYPES.error,
          this.isCheckoutPage
            ? this.$t('errors.prevent_gift_card_number_checkout')
            : this.$t('errors.prevent_gift_card_number')
        )
        return
      }
      this.isSubmitting = true
      const [error, result] = await to(this.$store.dispatch('order/applyPromoCode', this.code))
      const { type, text } = error || result
      if (result) this.isAdding = !this.hasPromoCode
      this.isSubmitting = false
      this.showFlashMessage(type, text)
    },
  },
}
</script>

<style lang="scss" scoped>
.promo-code {
  margin: 0;

  &__add {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    margin-top: 16px;
    border: none;

    .promo-code--default & {
      flex-direction: column;

      @media ($mq-medium) {
        flex-direction: row;
      }
    }
  }

  &__plus {
    margin-left: 8px;
    font-size: $font-size-xsmall;
  }

  &__submit-btn {
    .promo-code--default & {
      @media ($mq-medium) {
        width: auto;
        padding-left: 40px;
        padding-right: 40px;
      }
    }

    .promo-code--tall & {
      width: 55px;
      height: 55px;
      padding: 18px 12px 15px;

      svg {
        height: 16px;
      }
    }
  }

  &__field {
    flex: 1;
    margin: 0 10px 0 0;

    .promo-code--default & {
      width: 100%;
      margin: 0 0 16px 0;

      @media ($mq-medium) {
        flex: 1;
        margin: 0 10px 0 0;
      }
    }
  }

  .promo-code-fade-enter-active,
  .promo-code-fade-leave-active {
    transition: opacity 0.18s ease-in-out;
  }
  .promo-code-fade-enter,
  .promo-code-fade-leave-to {
    opacity: 0;
  }
}
</style>
